import React from "react";
//#region redux
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { ReceiptFormTypes } from "models/formTypes";
import { IconDefinitionConstants } from "utils/constants";
import { ChartEnums } from "models/enums";
//#endregion
//#region components
import {
  ChartToggle,
  DashboardButtonGroup,
  IconTitle,
  MasonryPanel,
  PageLayouts,
  RouteBreadCrumb,
  StyledButton,
  useMountedState,
  ViewPanel,
  WidgetArea,
} from "components/common";
import useUpdatePaymentMethodDialog from "./forms/useUpdatePaymentDialog";
import PaymentMethodDetails from "./generalDetails";
import ReceiptListView from "../listView";
import ReceiptCategoriesDistributionChart from "../widgets/categoriesDistribution";
import ReceiptMonthlyPurchasesDistributionChart from "../widgets/monthlyPurchasesDistribution";
import { useReceiptPaymentMethodService } from "serviceHooks";
import useReceiptPermissions from "../usePermissions";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const PaymentMethodProfile: React.FC = () => {
  const receiptPaymentMethodService = useReceiptPaymentMethodService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const updatePaymentMethodDialog = useUpdatePaymentMethodDialog();

  const { getMounted } = useMountedState();
  const urlParams = useParams<EntityIdParams>();
  const id = Number.parseInt(urlParams.id ?? "0");

  const [paymentMethod, setPaymentMethod] = React.useState<ApiModel.ReceiptPaymentMethod>();

  const { receiptViewer } = useReceiptPermissions();

  const title = `${paymentMethod?.name ?? ""} ${
    paymentMethod?.currencyMarkup ? paymentMethod?.currencyMarkup + "%" : ""
  }`;
  const subtitle = `${receiptPaymentMethodService.getTypeText(paymentMethod?.type) ?? ""}`;

  const getPaymentMethod = () => {
    if (id > 0)
      receiptPaymentMethodService.get(id).then((result) => {
        if (getMounted()) {
          setPaymentMethod(result.data);
        }
      });
  };

  React.useEffect(() => {
    getPaymentMethod();
  }, [id]);

  const filter: ReceiptFormTypes.Filter = React.useMemo(() => ({ paymentMethod: id }), [id]);

  const chartFilter: Omit<ApiModel.ReceiptFilter, keyof WintUtilsWebApi.Filter> = {
    paymentMethods: [id],
    createdToDate: DateHelper.today(),
    createdFromDate: DateHelper.addYears(DateHelper.today(), -1),
  };

  const onSettingsClick = () => {
    dispatch(displayDialog(updatePaymentMethodDialog({ ...paymentMethod })));
  };

  const topRightButtons = (
    <DashboardButtonGroup
      buttons={[
        <StyledButton size="sm" onClick={onSettingsClick}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Settings)}
        </StyledButton>,
      ]}
    />
  );

  return (
    <PageLayouts.DashboardLayout
      entityKey={id}
      hasPermissions={receiptViewer}
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={title ?? ""} />}
      title={<IconTitle title={title} subtitle={subtitle} icon={<></>} defaultTagSurface="background"></IconTitle>}
      topRightButtons={topRightButtons}
    >
      <MasonryPanel>
        <PaymentMethodDetails paymentMethod={paymentMethod} />
        <ViewPanel
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Payments)}
          appearance="sm"
          gridSize="lg"
          topRightContent={<ChartToggle />}
        >
          <ReceiptMonthlyPurchasesDistributionChart filter={chartFilter} />
        </ViewPanel>
        <WidgetArea>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Last12Months)}
            appearance="sm"
            gridSize="md"
          >
            <ReceiptCategoriesDistributionChart
              filter={{
                ...chartFilter,
              }}
              legendType={ChartEnums.LegendType.SMALL}
              showTotal={false}
            />
          </ViewPanel>
        </WidgetArea>
      </MasonryPanel>
      <ViewPanel
        title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Payments)}
        icon={IconDefinitionConstants.WTransactions}
      >
        <ReceiptListView filter={filter} hiddenColumns={["paymentMethodName"]} />
      </ViewPanel>
    </PageLayouts.DashboardLayout>
  );
};

export default PaymentMethodProfile;
